import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  TrendingUp,
  BarChart3,
  Settings,
  User,
  ChevronLeft,
  ChevronRight,
  Menu,
  Bot,
  TrendingDown,
  Building2,
  ChevronDown,
  ChevronUp,
  LogOut,
  ThumbsUp,
  Youtube,
  FileText
} from 'lucide-react';
import { logout, getUserRole, getAllowedPages } from '../services/api';

const Layout = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Persist collapsed state in localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [autoTradingExpanded, setAutoTradingExpanded] = useState(true);
  const location = useLocation();

  // Save collapsed state to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed);
  }, [isCollapsed]);

  const userRole = getUserRole();
  const allowedPages = getAllowedPages();

  // Check if user has access to a specific page
  const hasAccess = (pageKey) => {
    if (allowedPages.includes('all')) return true;
    return allowedPages.includes(pageKey);
  };

  // Navigation items with page-based access
  const allNavItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', pageKey: 'dashboard' },
    { icon: Settings, label: 'Settings', path: '/settings', pageKey: 'settings' },
    { icon: Youtube, label: 'Social Sentiment', path: '/social-sentiment', pageKey: 'social-sentiment' },
    { icon: BarChart3, label: 'Indexes', path: '/indexes', pageKey: 'indexes' },
    { icon: FileText, label: 'Reports', path: '/reports', pageKey: 'reports' },
    {
      icon: Bot,
      label: 'Auto Trading',
      isParent: true,
      pageKey: 'auto-trading',
      subItems: [
        { icon: Building2, label: 'Exchanges & Allocations', path: '/auto-trading' },
        { icon: Users, label: 'Connected User Accounts', path: '/connected-user-accounts' },
      ]
    },
  ];

  // Filter navigation items based on allowed pages
  const navItems = allNavItems.filter(item => hasAccess(item.pageKey));

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div className="min-h-screen bg-dark-900 flex">
      {/* Sidebar */}
      <div className={`${isCollapsed ? 'w-16' : 'w-72'} bg-gradient-to-b from-dark-800 via-dark-800 to-dark-900 border-r border-dark-600/50 flex flex-col sidebar-fixed sidebar-transition z-30 shadow-xl`}>
        {/* Logo and Toggle */}
        <div className={`${isCollapsed ? 'p-4' : 'p-6'} border-b border-dark-600/30 transition-all duration-300 bg-gradient-to-r from-dark-700/50 to-transparent`}>
          <div className={`flex items-center ${isCollapsed ? 'flex-col space-y-2' : 'justify-between'}`}>
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg ring-2 ring-blue-500/20">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              {!isCollapsed && (
                <div className="flex flex-col">
                  <span className="text-xl font-bold text-white">MarketVibes</span>
                  <span className="text-xs text-blue-400 font-medium">Admin Panel</span>
                </div>
              )}
            </div>
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-dark-600/50 text-dark-400 hover:text-white transition-all duration-200 hover:shadow-md"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-5">
          <div className={`${isCollapsed ? '' : 'mb-6'}`}>
            {!isCollapsed && (
              <h3 className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-4">Navigation</h3>
            )}
            <ul className="space-y-1">
              {navItems.map((item, index) => {
                if (item.isParent) {
                  const isAutoTradingActive = item.subItems.some(subItem => location.pathname === subItem.path);
                  return (
                    <li key={index}>
                      <button
                        onClick={() => !isCollapsed && setAutoTradingExpanded(!autoTradingExpanded)}
                        className={`w-full group flex items-center ${isCollapsed ? 'justify-center p-3' : 'justify-between px-4 py-3'} rounded-lg transition-colors duration-200 ${
                          isAutoTradingActive
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'text-dark-300 hover:bg-dark-700 hover:text-white'
                        }`}
                        title={isCollapsed ? item.label : ''}
                      >
                        <div className={`flex items-center ${isCollapsed ? '' : 'space-x-3'}`}>
                          <item.icon
                            size={isCollapsed ? 22 : 20}
                            strokeWidth={2.5}
                            className="flex-shrink-0"
                          />
                          {!isCollapsed && (
                            <span className="font-semibold">{item.label}</span>
                          )}
                        </div>
                        {!isCollapsed && (
                          <div className={`transition-transform duration-200 ${autoTradingExpanded ? 'rotate-180' : ''}`}>
                            <ChevronDown size={16} />
                          </div>
                        )}
                      </button>

                      {/* Sub-items */}
                      {!isCollapsed && autoTradingExpanded && (
                        <ul className="mt-2 ml-2 space-y-1 border-l-2 border-dark-600/30 pl-4">
                          {item.subItems.map((subItem, subIndex) => {
                            const isSubActive = location.pathname === subItem.path;
                            return (
                              <li key={subIndex}>
                                <Link
                                  to={subItem.path}
                                  className={`group flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                                    isSubActive
                                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md'
                                      : 'text-dark-400 hover:bg-dark-700/50 hover:text-white hover:shadow-sm'
                                  }`}
                                >
                                  <div className={`p-0.5 rounded ${isSubActive ? 'bg-white/20' : 'group-hover:bg-blue-500/20'} transition-colors`}>
                                    <subItem.icon size={16} />
                                  </div>
                                  <span className="font-medium text-sm">{subItem.label}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                } else {
                  const isActive = location.pathname === item.path || (item.path === '/dashboard' && location.pathname === '/');
                  return (
                    <li key={index}>
                      <Link
                        to={item.path}
                        className={`group flex items-center ${isCollapsed ? 'justify-center p-3' : 'space-x-3 px-4 py-3'} rounded-lg transition-colors duration-200 ${
                          isActive
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'text-dark-300 hover:bg-dark-700 hover:text-white'
                        }`}
                        title={isCollapsed ? item.label : ''}
                      >
                        <item.icon
                          size={isCollapsed ? 22 : 20}
                          strokeWidth={2.5}
                          className="flex-shrink-0"
                        />
                        {!isCollapsed && (
                          <span className="font-semibold">{item.label}</span>
                        )}
                      </Link>
                    </li>
                  );
                }
              })}
            </ul>
          </div>
        </nav>

        {/* User Profile */}
        <div className="p-5 border-t border-dark-600/30 bg-gradient-to-r from-dark-700/30 to-transparent">
          {isCollapsed ? (
            <div className="flex flex-col items-center space-y-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <User size={18} className="text-white" />
              </div>
              <button
                onClick={logout}
                className="p-2 hover:bg-red-600/50 rounded-lg transition-all duration-200 group"
                title="Logout"
              >
                <LogOut size={16} className="text-dark-400 group-hover:text-white transition-all duration-200" />
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-dark-700/50 rounded-xl p-4 backdrop-blur-sm border border-dark-600/20">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg ring-2 ring-blue-500/20">
                    <User size={18} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">
                      {localStorage.getItem('adminUsername') || 'Admin User'}
                    </p>
                    <p className="text-xs text-blue-300/80 truncate">{userRole === 'admin' ? 'Administrator' : 'Viewer'}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={logout}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white transition-all duration-200 group border border-red-600/20 hover:border-red-600"
              >
                <LogOut size={16} />
                <span className="font-medium text-sm">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col overflow-hidden main-content-offset ${isCollapsed ? 'ml-16' : 'ml-72'}`}>
        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;